import { scrollToTop } from 'frontend/shared/javascripts/util/scroll'
import { mobileBreakpoint } from 'shared/javascripts/util/mobile_breakpoint'

type PlayerDisplayMode = 'inline' | 'sticky-expanded' | 'sticky-collapsed'
type ToggleBarMode = 'hidden' | 'shown'

up.compiler('[data-player-toggle]', (element) => {
  const closePlayerAction = element.querySelector('.player-toggle-content--collapse')!
  const playerContainer = element.querySelector('.player-toggle-content')!
  const playerToggleBar = element.querySelector('.player-toggle-bar')!
  const showPlayerAction = element.querySelector('[player-show]')!
  const showQuizAction = element.querySelector('[quiz-show]')!

  const quiz = element.querySelector<HTMLElement>('.player-toggle-content--inner.-quiz')!
  const videoPlayer = element.querySelector<HTMLElement>('.player-toggle-content--inner.-video')!
  const videoToggleContainer = element.querySelector<HTMLElement>('.player-toggle-content')!

  let mobileInlineForced = false

  let quizObserver: IntersectionObserver
  if (quiz) {
    quizObserver = new IntersectionObserver((entries) => {
      if (hidden(quiz)) return

      if (entries[0].isIntersecting) {
        playerToggleBar.classList.remove('-visible')
      } else {
        playerToggleBar.classList.add('-visible')
      }
    })

    quizObserver.observe(quiz)
    up.on(showQuizAction, 'click', showQuiz)
  }

  up.on('scroll', { passive: true }, updatePlayerContainerState)
  up.on(closePlayerAction, 'click', closePlayer)
  up.on('player:force-mobile-inline', onForceMobileInline)
  up.on('player:release-mobile-inline', onReleaseMobileInline)
  up.on(showPlayerAction, 'click', showPlayer)
  mobileBreakpoint.addEventListener('change', updatePlayerContainerState)
  updatePlayerContainerState()

  function onForceMobileInline() {
    mobileInlineForced = true
    updatePlayerContainerState()
  }

  function onReleaseMobileInline() {
    mobileInlineForced = false
    updatePlayerContainerState()
  }

  function updatePlayerContainerState() {
    if (window.scrollY <= 20 || (mobileBreakpoint.matches && mobileInlineForced)) {
      setPlayerDisplayMode('inline')
      setToggleBarMode('hidden')
      return
    }

    if (videoVisible()) {
      setPlayerDisplayMode('sticky-expanded')
      setToggleBarMode('hidden')
      return
    }

    setPlayerDisplayMode('sticky-collapsed')
  }

  function closePlayer(event: Event) {
    const scrollY = window.scrollY
    const videoHeight = videoPlayer.getBoundingClientRect().height

    setPlayerDisplayMode('sticky-collapsed')
    setToggleBarMode('shown')

    // Due to scroll event listening and scrollbar collapse: If we have not scrolled out the video from the viewport
    // the closing of the player may just expand the video or in worst case expand and stick it again
    if (scrollY < videoHeight) window.scrollTo(0, playerToggleBar.getBoundingClientRect().top)

    up.emit('app:main-player:pause')
    up.event.halt(event)
  }

  function showPlayer(event: Event, element: Element) {
    setPlayerDisplayMode('sticky-expanded')
    setToggleBarMode('hidden')

    up.emit(element, 'player:switch')
    up.event.halt(event)
  }

  function showQuiz(event: Event, element: Element) {
    scrollToTop()
    setPlayerDisplayMode('sticky-collapsed')
    setToggleBarMode('hidden')

    up.emit(element, 'player:switch')
    up.event.halt(event)
  }

  function setPlayerDisplayMode(mode: PlayerDisplayMode) {
    if (mode === 'inline') {
      playerContainer.classList.remove('-sticky')
      playerContainer.classList.remove('-collapsed')
      playerContainer.removeAttribute('up-fixed')
      return
    }

    playerContainer.classList.add('-sticky')
    playerContainer.setAttribute('up-fixed', 'top')
    playerContainer.classList.toggle('-collapsed', mode === 'sticky-collapsed')
  }

  function setToggleBarMode(mode: ToggleBarMode) {
    playerToggleBar.classList.toggle('-visible', mode === 'shown')
  }

  function videoVisible() {
    return visible(videoPlayer) && visible(videoToggleContainer) // Check for container and the player since the display property is not inherited
  }

  function visible(node: HTMLElement) {
    return window.getComputedStyle(node).display !== 'none'
  }

  function hidden(node: HTMLElement) {
    return !visible(node)
  }

  return () => {
    if (quizObserver) quizObserver.unobserve(quiz)
    up.off('scroll', updatePlayerContainerState)
    up.off('player:force-mobile-inline', onForceMobileInline)
    up.off('player:release-mobile-inline', onReleaseMobileInline)
    mobileBreakpoint.removeEventListener('change', updatePlayerContainerState)
  }
})
